import { useParams, useSearch } from '@tanstack/react-router'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType, TaggableUser } from 'types'

/**
 * Hook to fetch mentionable users from both organization and site level user lists
 * Returns the union of users from both lists
 * Used for @ mention functionality in rich text editor
 * @returns SWR response with TaggableUser array
 */
export const useMentionableUsers = () => {
  const params = useParams({ strict: false })
  const search = useSearch({ from: '__root__' })

  const siteId = params.siteID ?? search.siteId

  const { getSite } = useRolodexEntries({
    type: RolodexEntryType.SITE,
  })

  const orgId = useMemo(() => {
    if (params.orgID ?? search.orgId) {
      return params.orgID ?? search.orgId
    }
    if (siteId) {
      const site = getSite(siteId)
      return site?.tags[RolodexEntryType.ORGANIZATION]
    }
    return undefined
  }, [params.orgID, search.orgId, siteId, getSite])

  const {
    userLists: allUserLists,
    isLoading,
    error: fetchError,
  } = useRolodexEntries({
    type: RolodexEntryType.USER_LIST,
    tags: {
      [RolodexEntryType.ORGANIZATION]: orgId,
    },
  })

  const { data, error } = useMemo(() => {
    if (isLoading) {
      return { data: [], error: null }
    }

    const relevantUserLists = allUserLists.filter((list) => {
      const listOrgId = list.tags[RolodexEntryType.ORGANIZATION]
      const listSiteId = list.tags[RolodexEntryType.SITE]

      return (orgId && listOrgId === orgId) || (siteId && listSiteId === siteId)
    })

    const userMap = new Map<string, TaggableUser>()
    relevantUserLists.forEach((list) => {
      const users = list.data.users || []
      users.forEach((user) => {
        userMap.set(user.id, user)
      })
    })

    return {
      data: Array.from(userMap.values()),
      error: null,
    }
  }, [siteId, orgId, isLoading, allUserLists])

  return {
    data: data as TaggableUser[],
    error: error || (fetchError ? String(fetchError) : null),
    isLoading,
  }
}
